import { IapOfferCodeMappingCreateSchema, IapOfferCodeMappingUpdateSchema } from '@aysar/contracts';
import { z } from 'zod';

/**
 * The offer-code forms' schemas — the contract's, with its defaults removed.
 *
 * ## Why the contract's own schema cannot be mounted
 *
 * `useDynamicFormLogic` takes `ZodType<TValues, TValues>` and says why in as
 * many words: `zodResolver` requires the schema's **input** to be the form's
 * value shape, and a schema carrying a `.default()` has an input that admits
 * `undefined` where its output does not — so the form would edit one shape while
 * the resolver validated another. Removing the defaults from the contract
 * instead would change what a `POST` accepts (P23b1).
 *
 * ## Two schemas, because the api offers two different writes
 *
 * `discountRuleId · productKey · store` are the mapping's **identity** and the
 * unique index over it. `IapOfferCodeMappingUpdateSchema` omits all three on
 * purpose: re-pointing a code at a different rule is a *different mapping*, not
 * an edit. So the create is six fields and the edit is three — two field sets,
 * never one with half the controls disabled.
 *
 * `offerCode` is **not** normalised anywhere on this path. Apple's codes are
 * case-sensitive, so a `transform` here would silently break every mixed-case
 * code pasted out of App Store Connect.
 */
const create = IapOfferCodeMappingCreateSchema.shape;

export const OfferCodeCreateFormSchema = z
  .object({
    discountRuleId: create.discountRuleId,
    productKey: create.productKey,
    store: create.store,
    offerCode: create.offerCode,
    isActive: create.isActive.removeDefault(),
    // Already nullable, so unwrapping the default is all that is needed — the
    // form always supplies the key, `null` included.
    note: create.note.removeDefault(),
  })
  .strict();

export type OfferCodeCreateFormValues = z.infer<typeof OfferCodeCreateFormSchema>;

/**
 * Editing a mapping: **the code, never what it points at.**
 *
 * `.partial()` is undone here rather than kept: a PATCH may legitimately omit a
 * key, but a **form** that omits one is a control the operator can see and
 * cannot fill, and the editor seeds all three from the row it re-read.
 */
const update = IapOfferCodeMappingUpdateSchema.shape;

export const OfferCodeEditFormSchema = z
  .object({
    offerCode: update.offerCode.unwrap(),
    isActive: update.isActive.unwrap().removeDefault(),
    note: update.note.unwrap().removeDefault(),
  })
  .strict();

export type OfferCodeEditFormValues = z.infer<typeof OfferCodeEditFormSchema>;
